// src/hooks/useReferralBonus.js
import { useState, useEffect, useContext, useCallback } from 'react';
import { applyReferralCode } from '../services/authApi';
import AuthContext from '../context/AuthContext';
import posthog from 'posthog-js';

/**
 * Custom hook to handle referral bonus logic
 * Checks for referral code in localStorage and applies it if user is eligible
 *
 * @returns {Object} - { showBonusModal, closeBonusModal, bonusDetails }
 */
export const useReferralBonus = () => {
    const authContext = useContext(AuthContext);
    const [showBonusModal, setShowBonusModal] = useState(false);
    const [bonusDetails, setBonusDetails] = useState({ creditsAdded: 0, tokensAdded: 0 });
    const [hasChecked, setHasChecked] = useState(false);

    const closeBonusModal = useCallback(() => {
        setShowBonusModal(false);
    }, []);

    const checkAndApplyReferralCode = useCallback(async () => {
        // Only check once per session
        if (hasChecked) {
            return;
        }

        // Only check if user is logged in
        if (!authContext.isLoggedIn || !authContext.token) {
            return;
        }

        setHasChecked(true);

        try {
            // Check if there's a referral code in localStorage
            const referralCode = localStorage.getItem('referralCode');

            if (!referralCode) {
                return;
            }

            // Check if the referral code was stored recently (within last 24 hours)
            const referralCodeTimestamp = localStorage.getItem('referralCodeTimestamp');
            if (referralCodeTimestamp) {
                const timeDiff = Date.now() - parseInt(referralCodeTimestamp);
                const hoursDiff = timeDiff / (1000 * 60 * 60);

                // If code is older than 24 hours, ignore it
                if (hoursDiff > 24) {
                    localStorage.removeItem('referralCode');
                    localStorage.removeItem('referralCodeTimestamp');
                    return;
                }
            }

            // Try to apply the referral code
            const response = await applyReferralCode(authContext.token, referralCode);

            if (response.success) {
                // Store bonus details for the modal
                setBonusDetails({
                    creditsAdded: response.credits_added || 10,
                });

                // Update user's credits in the context
                if (authContext.user) {
                    authContext.updateUserCredit(10);
                }

                // Show the bonus modal
                setShowBonusModal(true);

                // Track the referral conversion with PostHog
                if (typeof posthog !== 'undefined') {
                    posthog.capture('referral_bonus_claimed', {
                        referral_code: referralCode,
                        credits_added: response.credits_added,
                        tokens_added: response.tokens_added,
                        user_id: authContext.user?.sub,
                        timestamp: new Date().toISOString()
                    });
                }

                // Clear the referral code from localStorage after successful application
                localStorage.removeItem('referralCode');
                localStorage.removeItem('referralCodeTimestamp');
            }
        } catch (error) {
            console.error('Error applying referral code:', error);

            // If the error is that the user already claimed, just clear the code silently
            if (error.data?.message?.includes('already claimed')) {
                localStorage.removeItem('referralCode');
                localStorage.removeItem('referralCodeTimestamp');
            }
            // Don't show error to user - fail silently for referral codes
        }
    }, [authContext, hasChecked]);

    // Check for referral code when user logs in
    useEffect(() => {
        if (authContext.isLoggedIn && authContext.user && !hasChecked) {
            // Small delay to ensure user data is fully loaded
            const timer = setTimeout(() => {
                checkAndApplyReferralCode();
            }, 1000);

            return () => clearTimeout(timer);
        }
    }, [authContext.isLoggedIn, authContext.user, hasChecked, checkAndApplyReferralCode]);

    return {
        showBonusModal,
        closeBonusModal,
        bonusDetails
    };
};

export default useReferralBonus;
